import React, { useState, useEffect, useMemo } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { eventService } from '@/services/eventService';
import { format, startOfMonth, endOfMonth, isSameMonth } from 'date-fns';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { User, Search, ChevronDown, X, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { BASE_URL } from '@/config/api';
import { useIsMobile } from '@/hooks/use-mobile';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle
} from '@/components/ui/card';
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage
} from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ArrowLeft, Calendar as CalendarIcon, CreditCard, Trash, AlertCircle, Plus, Info, Tag, FileText, Euro
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DateRange as RDPDateRange } from 'react-day-picker';
import { Switch } from '@/components/ui/switch';
import { currencies } from '@/utils/ip-detection';


interface InvoiceItem {
  id: string;
  title: string;
  description: string;
  price: number;
  priceInput?: string;
  date?: Date;
}

interface TaxItem {
  id: string;
  name: string;
  rate: number;
  selected: boolean;
}

interface AdditionalFee {
  description: string;
  amount: number;
}

type DateRange = Partial<RDPDateRange>;

const invoiceSchema = z.object({
  invoiceNumber: z.string().optional(),
  invoiceDate: z.date(),
  dueDate: z.date(),
  discountType: z.enum(['none', 'percentage', 'amount']).default('none'),
  discountValue: z.number().min(0).default(0),
  note: z.string().optional(),
});

interface TeacherSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teachers: any[];
  selectedTeacherIds: string[];
  onSelectTeacher: (ids: string[]) => void;
  user: any;
  t: (key: string) => string;
}

const TeacherSelectorDialog: React.FC<TeacherSelectorDialogProps> = ({
  open,
  onOpenChange,
  teachers,
  selectedTeacherIds,
  onSelectTeacher,
  user,
  t
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentSelectedIds, setCurrentSelectedIds] = useState(selectedTeacherIds);
  
  const ownSchool = user?.schools?.find(s => s.school_type === "C");
  const [selectedSchoolId, setSelectedSchoolId] = useState(() => {
    return ownSchool?.id || user?.schools?.[0]?.id || null;
  });

  useEffect(() => {
    setCurrentSelectedIds(selectedTeacherIds);
  }, [selectedTeacherIds, open]);

  const getFilteredTeachers = () => {
    if (!searchQuery.trim()) {
      return teachers;
    }
    const lowerSearch = searchQuery.toLowerCase();
    return teachers.filter(teacher => {
      const fullName = teacher.name || `${teacher.firstname} ${teacher.lastname}`;
      return fullName.toLowerCase().includes(lowerSearch);
    });
  };

  const filteredTeachers = useMemo(() => {
    const filteredTeachersRaw = getFilteredTeachers();
    if (
      user?.account_type === "C" &&
      user?.roleType === "teachers_all" &&
      selectedSchoolId
    ) {
      return filteredTeachersRaw.filter(teacher => {
        if (teacher.school_origin === "old") {
          return selectedSchoolId === ownSchool?.id;
        }
        return teacher.school_id === selectedSchoolId;
      });
    }

    return filteredTeachersRaw;
  }, [searchQuery, teachers, selectedSchoolId, user, ownSchool]);

  const handleSelect = (teacherId: string) => {
    if (currentSelectedIds.includes(teacherId)) {
      setCurrentSelectedIds(currentSelectedIds.filter((id: string) => id !== teacherId));
    } else {
      setCurrentSelectedIds([...currentSelectedIds, teacherId]);
    }
  };

  const handleDone = () => {
    onSelectTeacher(currentSelectedIds);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[600px]">
        <div className="p-6 w-auto min-h-[38vh]">
          <div className="flex justify-start items-center mb-4">
            <User className="mr-2" />
            <p className="text-lg font-semibold">
              {t('agenda.selectTeachers')}{' '}
              {teachers.length > 0 ? `(${teachers.length})` : ''}
            </p>
          </div>

          <div className="grid gap-4">
            {user?.account_type === "C" && user?.roleType === "teachers_all" && (
             <div className="relative bg-gray-50 p-2 rounded-md">
              <select
                value={selectedSchoolId || ""}
                onChange={(e) => setSelectedSchoolId(Number(e.target.value))}
                className="w-full border rounded-md p-2 pr-10 appearance-none"
              >
                {user?.schools?.map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.school_name}{school.school_type === "C" ? " (" + t('teachers.myTeachers') + ")" : ""}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </div>
            </div>
            )}

            <div className="flex items-center justify-between gap-4 bg-gray-50 p-2 rounded-md">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder={t('teachers.search')} 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 w-full"
                  autoFocus
                />
              </div>
            </div>

            <div className="max-h-[50vh] overflow-y-auto border rounded-md">
              {filteredTeachers.map(teacher => {
                const isSelected = currentSelectedIds.includes(teacher.id);
                const displayName = teacher.name || `${teacher.firstname} ${teacher.lastname}`;

                return (
                  <div
                    key={teacher.id}
                    className={`flex items-center p-2 border-b last:border-b-0 cursor-pointer hover:bg-gray-100 ${
                      isSelected ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleSelect(teacher.id)}
                  >
                    <div className={`h-4 w-4 border rounded-sm mr-2 flex items-center justify-center ${
                      isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <span className="flex-1">{displayName}</span>
                  </div>
                );
              })}

              {filteredTeachers.length === 0 && (
                <div className="p-4 text-center text-muted-foreground">
                  {t('agenda.noTeacherFound')}
                </div>
              )}
            </div>

            <div className="flex justify-end items-center space-x-2 mt-4">
              <Label>
                {t('teachers.selected')}: {currentSelectedIds.length || 0}
              </Label>
              <Button onClick={handleDone}>
                {t('common.done')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


const InvoiceTeacherManualCreate = () => {
  const { t, locale } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [selectedTeacherIds, setSelectedTeacherIds] = useState<string[]>([]);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([]);
  const [newItem, setNewItem] = useState<{ title: string; description: string; price: string; date?: Date }>({ title: '', description: '', price: '' });
  const [showTeacherSelector, setShowTeacherSelector] = useState(false);

  const [additionalFees, setAdditionalFees] = useState<AdditionalFee[]>([]);
  const [taxes, setTaxes] = useState<TaxItem[]>([]);
  const [discountType, setDiscountType] = useState<'none' | 'percentage' | 'amount'>('none');
  const [discountValue, setDiscountValue] = useState<number>(0);
  const [discountInput, setDiscountInput] = useState('');
  
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<Date | undefined>(undefined);
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<string>('weekly');

  const [billingPeriodRange, setBillingPeriodRange] = useState<DateRange>({ from: undefined, to: undefined });
  const [billingCalendarMonth, setBillingCalendarMonth] = useState<Date>(new Date());
  
  const [isCreatingInvoice, setIsCreatingInvoice] = useState<boolean>(false);
  const isMobile = useIsMobile();
  
  const dueDatePreference = Number(user.teacher.due_date_preference || 7); 
  const [invoiceDateMonth, setInvoiceDateMonth] = useState<Date>(new Date());
  const [recurrenceEndDateMonth, setRecurrenceEndDateMonth] = useState<Date>(new Date());
  const [dueDateMonth, setDueDateMonth] = useState<Date>(new Date());
  const [manualCurrencyCode, setManualCurrencyCode] = useState(user.currency_code);

  const queryClient = useQueryClient();

  const calculateDueDate = () => {
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + dueDatePreference);
    return dueDate;
  };

  const form = useForm<z.infer<typeof invoiceSchema>>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      invoiceNumber: `INV-${Date.now().toString().substring(7)}`,
      invoiceDate: new Date(),
      dueDate: calculateDueDate(),
      discountType: 'none',
      discountValue: 0,
      note: '',
    },
  });

  const { data: teachers = [], isLoading: teachersLoading } = useQuery<any[]>({
    queryKey: ['teachers', token],
    queryFn: () => eventService.getTeachers(user, token),
    enabled: !!token,
  });

  const { data: taxData, isLoading: taxesLoading } = useQuery({
    queryKey: ['taxes', token],
    queryFn: async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/billing_taxes`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Accept' : 'application/json',
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Failed to fetch taxes');
        const data = await response.json();
        return data.taxes || [];
      } catch (error) {
        console.error('Error fetching taxes:', error);
        return [];
      }
    },
  });

  const removeAccents = (str) => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  const invoiceDate = form.watch("invoiceDate");

  useEffect(() => {
    if (selectedTeacherIds.length === 1 && invoiceDate) {
      const selectedTeacher = teachers.find(t => t.id === selectedTeacherIds[0]);
      if (!selectedTeacher) return;

      let month = invoiceDate.toLocaleString('default', { month: 'short' });
      month = removeAccents(month).replace('.', '');
      const year = invoiceDate.getFullYear();
      let newInvoiceNumber;
      if (selectedTeacher.firstname && selectedTeacher.firstname.trim() !== '') {
        const firstName = selectedTeacher.firstname.toLowerCase().replace(/\s+/g, '');
        const lastName = selectedTeacher.lastname.toLowerCase().replace(/\s+/g, '');
        newInvoiceNumber = `${firstName}-${lastName}-${month}-${year}`;
      } else if (selectedTeacher.lastname && selectedTeacher.lastname.trim() !== '') {
        const lastName = selectedTeacher.lastname.toLowerCase().replace(/\s+/g, '');
        newInvoiceNumber = `${lastName}-${month}-${year}`;
      } else {
        newInvoiceNumber = `${month}-${year}`;
      }
      form.setValue('invoiceNumber', newInvoiceNumber);
    }
  }, [selectedTeacherIds, invoiceDate, form, teachers]);

  useEffect(() => {
    if (taxData) {
      const formattedTaxes = taxData.map((tax: any) => ({
        id: tax.id,
        name: tax.name,
        rate: tax.rate,
        selected: tax.selected || tax.id === '1',
        amount: 0,
      }));
      setTaxes(formattedTaxes);
    }
  }, [taxData]);
  
  const handleToggleTax = (taxId: string) => {
    setTaxes((prev) =>
      prev.map((tax) =>
        tax.id === taxId ? { ...tax, selected: !tax.selected } : tax
      )
    );
  };

  const addInvoiceItem = () => {
    if ((!isRecurring && !newItem.date) || (!isRecurring && !newItem.title) || (isRecurring && !newItem.title) || !newItem.price) {
        toast({
            variant: "destructive",
            title: t('common.error'),
            description: t('invoices.itemValidationError'),
        });
        return;
    }
    const priceNum = parseFloat(newItem.price.replace(',', '.'));
    if (isNaN(priceNum) || priceNum <= 0) {
        toast({
            variant: "destructive",
            title: t('common.error'),
            description: t('invoices.itemPriceInvalid'),
        });
        return;
    }

    setInvoiceItems([...invoiceItems, { ...newItem, id: Date.now().toString(), price: priceNum, description: newItem.title + (newItem.description ? ' (' + newItem.description + ')' : '') }]);
    setNewItem({ title: '', description: '', price: '', date: undefined });
  };

  const removeInvoiceItem = (id: string) => {
    setInvoiceItems(invoiceItems.filter(item => item.id !== id));
  };

  const getSubtotal = () => {
    return invoiceItems.reduce((sum, item) => sum + item.price, 0);
  };

  const getDiscountAmount = () => {
    const subtotal = getSubtotal();
    if (discountType === 'none' || discountValue === 0) return 0;
    if (discountType === 'percentage') return (subtotal * discountValue) / 100;
    return discountValue;
  };

  const getTaxAmount = () => {
    const subtotal = getSubtotal();
    const discount = getDiscountAmount();
    const taxableAmount = subtotal - discount;
    return taxes
      .filter((tax) => tax.selected)
      .reduce((sum, tax) => sum + (taxableAmount * tax.rate) / 100, 0);
  };

  useEffect(() => {
    const subtotal = getSubtotal();
    const discount = getDiscountAmount();
    const taxableAmount = subtotal - discount;

    setTaxes((prevTaxes) =>
      prevTaxes.map((tax) => ({
        ...tax,
        amount: tax.selected ? (taxableAmount * tax.rate) / 100 : 0,
      }))
    );
  }, [invoiceItems, discountType, discountValue, JSON.stringify(taxes.map(t => t.selected))]);

  const addAdditionalFee = () => {
    setAdditionalFees([
      ...additionalFees,
      { description: "", amount: 0 }
    ]);
  };

  const removeAdditionalFee = (index: number) => {
    setAdditionalFees(additionalFees.filter((_, i) => i !== index));
  };

  const handleAdditionalFeeChange = (index: number, field: keyof AdditionalFee, value: string | number) => {
    const updatedFees = [...additionalFees];
    if (field === 'description') {
      updatedFees[index].description = value as string;
    } else if (field === 'amount') {
      updatedFees[index].amount = value as number;
    }
    setAdditionalFees(updatedFees);
  };

  const getAdditionalFeesTotal = () => {
    return additionalFees.reduce((sum, fee) => sum + (Number(fee.amount) || 0), 0);
  };

  const getTotal = () => {
    return getSubtotal() - getDiscountAmount() + getTaxAmount() + getAdditionalFeesTotal();
  };

  const translateStr = (key: string): string => {
    const translated = t(key);
    return Array.isArray(translated) ? translated.join(' ') : translated;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: user.currency_code || '$'
    }).format(amount);
  };

  const onSubmit = async (values: z.infer<typeof invoiceSchema>) => {
    if (isCreatingInvoice) return;
    const uniqueTeacherIds = Array.from(new Set(selectedTeacherIds));

    if (uniqueTeacherIds.length === 0) {
      toast({
        variant: "destructive",
        title: translateStr('invoices.noTeacherSelected'),
        description: translateStr('invoices.pleaseSelectTeacher'),
      });
      return;
    }
    if (invoiceItems.length === 0) {
      toast({
        variant: "destructive",
        title: translateStr('invoices.noItemsAdded'),
        description: translateStr('invoices.pleaseAddItems'),
      });
      return;
    }
    
    if (isRecurring && !recurrenceEndDate) {
      toast({
        variant: "destructive",
        title: translateStr('common.error'),
        description: translateStr('invoices.recurrentEndDateRequired'),
      });
      return;
    }

    let finalBillingPeriodRange = { ...billingPeriodRange };
    if (finalBillingPeriodRange.from && !finalBillingPeriodRange.to) {
      finalBillingPeriodRange.to = endOfMonth(finalBillingPeriodRange.from);
    }
    const adjustedBillingPeriod = {
      from: finalBillingPeriodRange.from ? format(finalBillingPeriodRange.from, 'yyyy-MM-dd') : undefined,
      to: finalBillingPeriodRange.to ? format(finalBillingPeriodRange.to, 'yyyy-MM-dd') : undefined,
    };

    setIsCreatingInvoice(true);
    try {
      let lastInvoiceId = null;
      let successCount = 0;

      for (const teacherId of uniqueTeacherIds) {
        const teacher = teachers.find(t => t.id === teacherId);
        let thisInvoiceNumber = values.invoiceNumber;

        if (uniqueTeacherIds.length > 1 && teacher) {
           let month = values.invoiceDate.toLocaleString('default', { month: 'short' });
           month = removeAccents(month).replace('.', '');
           const year = values.invoiceDate.getFullYear();
           if (teacher.firstname && teacher.firstname.trim() !== '') {
              const firstName = teacher.firstname.toLowerCase().replace(/\s+/g, '');
              const lastName = teacher.lastname.toLowerCase().replace(/\s+/g, '');
              thisInvoiceNumber = `${firstName}-${lastName}-${month}-${year}`;
           } else {
              thisInvoiceNumber = `${teacher.lastname.toLowerCase().replace(/\s+/g, '')}-${month}-${year}`;
           }
        }

        const invoiceData = {
          ...values,
          invoiceNumber: thisInvoiceNumber,
          billingPeriod: adjustedBillingPeriod,
          events: invoiceItems.map(item => ({
            id: item.id,
            title: isRecurring ? item.title : (item.description || translateStr('agenda.Lesson')),
            description: item.description,
            price: item.price,
            amount: item.price,
            date: isRecurring ? format(values.invoiceDate, 'yyyy-MM-dd') : (item.date ? format(item.date, 'yyyy-MM-dd') : format(values.invoiceDate, 'yyyy-MM-dd')),
            category: 'Manual Entry',
            selected: true,
          })),
          taxes: taxes.filter((tax) => tax.selected),
          subtotal: getSubtotal(),
          discount: {
            type: discountType,
            value: discountValue,
            amount: getDiscountAmount(),
          },
          taxAmount: getTaxAmount(),
          additionalFeesTotal: getAdditionalFeesTotal(),
          additionalFees: additionalFees,
          total: getTotal(),
          teacherId: [teacherId],
          isRecurring,
          recurrenceFrequency: isRecurring ? recurrenceFrequency : null,
          recurrenceEndDate: isRecurring && recurrenceEndDate ? format(recurrenceEndDate, 'yyyy-MM-dd') : null,
          currencyCode: manualCurrencyCode || 'USD',
        };

        const response = await fetch(`${BASE_URL}/api/invoices/school/manual/create`, {
          method: 'POST',
          headers: {
            'Accept' : 'application/json',
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify(invoiceData),
        });

        if (!response.ok) throw new Error('Failed to create invoice for teacher ' + teacherId);
        const data = await response.json();
        lastInvoiceId = data.invoiceId;
        successCount++;
      }

      if (successCount > 0) {
        toast({
          title: translateStr('invoices.invoiceCreated'),
          description: `${successCount} ${translateStr('invoices.invoiceCreatedDescription')}`,
        });
        
        if (uniqueTeacherIds.length === 1 && lastInvoiceId) {
          navigate(`/invoices/${lastInvoiceId}`);
        } else {
          navigate('/invoices');
        }
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({
        variant: "destructive",
        title: translateStr('invoices.invoiceCreationFailed'),
        description: translateStr('invoices.invoiceCreationFailedDescription'),
      });
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 container py-8 mt-16">
        <div>
          <Button variant="ghost" onClick={() => navigate('/invoices')} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {translateStr('common.back')}
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <Card className='bg-gray-50'>
              <CardHeader>
                <CardTitle>
                  <div className="flex items-center gap-3">
                    <User className="h-6 w-6 mt-2" />
                      <div className="w-full">
                        {teachersLoading ? (
                          <Skeleton className="h-10 w-full" />
                        ) : (
                          <Button
                              variant="outline"
                              onClick={() => setShowTeacherSelector(true)}
                              className="w-full justify-between"
                          >
                              {selectedTeacherIds.length > 0
                                ? `${selectedTeacherIds.length} ${
                                    (selectedTeacherIds.length > 1 
                                      ? t('teachers.teachers') 
                                      : t('teachers.teacher')
                                    )
                                  } ${t('teachers.selected')}`
                                : t('agenda.selectTeachers')}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        )}
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedTeacherIds.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[...new Set(selectedTeacherIds)]
                      .map((id) => teachers.find((t: any) => t.id === id))
                      .filter(Boolean)
                      .map((teacher: any) => (
                        <div
                          key={teacher.id}
                          className="bg-secondary text-secondary-foreground px-2 py-1 rounded-md text-sm flex items-center gap-1"
                        >
                          {teacher.firstname} {teacher.lastname}
                          <X
                            className="h-3 w-3 cursor-pointer hover:text-destructive"
                            onClick={() =>
                              setSelectedTeacherIds(
                                selectedTeacherIds.filter((id) => id !== teacher.id)
                              )
                            }
                          />
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateStr('invoices.items')}</CardTitle>
                <CardDescription>{translateStr('invoices.addItemsToInvoice')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-2 items-end mb-5">
                    {isRecurring ? (
                        <div className="relative">
                            <FileText className="absolute left-2 top-3.5 h-3 w-3 text-muted-foreground" />
                            <Input
                                value={newItem.title}
                                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                                placeholder={translateStr('agenda.createNew.title')}
                                className="pl-6"
                            />
                        </div>
                    ) : (
                        <div className="relative">
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" className={cn("w-full justify-start text-left font-normal pl-7 bg-gray-50", !newItem.date && "text-muted-foreground")}>
                                        <CalendarIcon className="absolute left-2 top-3 h-2 w-2 text-muted-foreground" />
                                        {newItem.date ? format(newItem.date, locale === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy') : <span>{translateStr('invoices.date')}</span>}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar mode="single" selected={newItem.date} onSelect={(date) => setNewItem({ ...newItem, date })} initialFocus />
                                </PopoverContent>
                            </Popover>
                        </div>
                    )}
                    
                    {!isRecurring ? (
                        <div className="relative">
                            <FileText className="absolute left-2 top-3.5 h-3 w-3 text-muted-foreground" />
                            <Input
                                value={newItem.title}
                                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                                placeholder={translateStr('agenda.createNew.title')}
                                className="pl-6"
                            />
                        </div>
                    ) : (
                    <div className="relative">
                        <FileText className="absolute left-2 top-3.5 h-3 w-3 text-muted-foreground" />
                        <Input
                            value={newItem.description}
                            onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                            placeholder={translateStr('invoices.description') + ' ('+ translateStr('agenda.optional') +')'} 
                            className="pl-6"
                        />
                    </div>
                    )}
                    <div className="relative">
                        <Input
                            value={newItem.price}
                            onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                            placeholder={translateStr('invoices.amount')}
                            type="text"
                            inputMode="decimal"
                            className="pl-2"
                        />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
                          {manualCurrencyCode || 'USD'}
                        </span>
                    </div>
                    <Button onClick={addInvoiceItem}><Plus className="h-4 w-4" /></Button>
                </div>
              
                {invoiceItems.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{isRecurring ? translateStr('agenda.createNew.title') : translateStr('invoices.date')}</TableHead>
                        {!isRecurring && (
                          <TableHead>{translateStr('agenda.createNew.title')}</TableHead>
                        )}
                        <TableHead className="text-right">{translateStr('invoices.amount')}</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoiceItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className={!isRecurring ? 'font-medium' : ''}>
                            {isRecurring ? item.description : (item.date ? format(item.date, locale === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy') : '-')}
                          </TableCell>
                          {!isRecurring && (
                            <TableCell>{item.description}</TableCell>
                          )}
                          <TableCell className="text-right">{formatCurrency(item.price)}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" onClick={() => removeInvoiceItem(item.id)}>
                              <Trash className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{translateStr('invoices.noItemsAdded')}</AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle>{translateStr('invoices.newInvoice')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form className="space-y-4">
                    <FormField control={form.control} name="invoiceNumber" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{translateStr('invoices.invoiceNumber')}</FormLabel>
                        <FormControl><Input {...field} /></FormControl>
                      </FormItem>
                    )} />

                    <div className="flex flex-col gap-4 pt-2 pb-3">
                    <div className="flex items-center space-x-2">
                        <Switch id="recurring" checked={isRecurring} onCheckedChange={(checked) => {
                          setIsRecurring(checked);
                          if (checked && !recurrenceEndDate) {
                            setRecurrenceEndDate(new Date());
                          }
                        }} />
                        <Label htmlFor="recurring">{t('invoices.recurrentInvoice')}</Label>
                    </div>
                    {isRecurring && (
                        <div className="pl-2 border-l-2 border-muted space-y-4">
                            <p className="text-sm text-muted-foreground pl-1">
                                {t('invoices.recurrentInvoiceDescription')}.
                            </p>
                            <div className="flex flex-col gap-2">
                                <Label>{t('invoices.recurrenceFrequency', "Fréquence de récurrence")}</Label>
                                <Select value={recurrenceFrequency} onValueChange={(value: string) => setRecurrenceFrequency(value)}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('invoices.recurrenceFrequency', "Sélectionner une fréquence")} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="weekly">{t('invoices.recurringFrequencyList.weekly',)}</SelectItem>
                                        <SelectItem value="biweekly">{t('invoices.recurringFrequencyList.biweekly',)}</SelectItem>
                                        <SelectItem value="monthly">{t('invoices.recurringFrequencyList.monthly',)}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex flex-col gap-2">
                                <Label>{t('invoices.recurrenceEndDate', "Date de fin de la récurrence")}</Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !recurrenceEndDate && "text-muted-foreground")}>
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {recurrenceEndDate ? format(recurrenceEndDate, locale === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy') : <span>{t('invoices.endDate', "Date de fin")}</span>}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar 
                                          mode="single" 
                                          selected={recurrenceEndDate} 
                                          onSelect={(date) => {setRecurrenceEndDate(date); if(date) setRecurrenceEndDateMonth(date);}} 
                                          month={recurrenceEndDateMonth} 
                                          onMonthChange={setRecurrenceEndDateMonth} 
                                          initialFocus 
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                    )}
                  </div>

                    {!isRecurring && (
                      <div>
                        <Label htmlFor="billing-period-range">
                            <div className="flex items-center">
                              {translateStr('invoices.billingPeriod')}
                              <TooltipProvider>
                                <Tooltip delayDuration={100}>
                                  <TooltipTrigger asChild>
                                    <Info className="h-4 w-4 text-muted-foreground ml-2 cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t('invoices.billingPeriodDateTooltip')}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button id="billing-period-range" variant="outline" className={cn("w-full justify-start text-left font-normal mt-2", !billingPeriodRange.from && "text-muted-foreground")}>
                              {billingPeriodRange.from ? (billingPeriodRange.to ? (`${format(billingPeriodRange.from, locale === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy')} - ${format(billingPeriodRange.to, locale === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy')}`) : format(billingPeriodRange.from, "LLL dd, y")) : (<span>{translateStr('invoices.pickDateRange')}</span>)}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar initialFocus mode="range" month={billingCalendarMonth} onMonthChange={setBillingCalendarMonth} selected={billingPeriodRange as RDPDateRange} onSelect={(range) => {
                                  const newRange = range || { from: undefined, to: undefined };
                                  if (newRange.from && newRange.to && !isSameMonth(newRange.from, newRange.to)) {
                                      setBillingPeriodRange({ from: newRange.from, to: undefined });
                                      setBillingCalendarMonth(newRange.from);
                                  } else {
                                      setBillingPeriodRange(newRange);
                                      if(newRange.from) setBillingCalendarMonth(newRange.from);
                                  }
                              }} disabled={(date) => { if (billingPeriodRange.from && !billingPeriodRange.to) return !isSameMonth(date, billingPeriodRange.from); return false; }} numberOfMonths={1} className="p-3"/>
                          </PopoverContent>
                        </Popover>
                      </div>
                    )}

                    {!isRecurring && (
                    <div className="grid grid-cols-2 gap-4">
                      <FormField control={form.control} name="invoiceDate" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{translateStr('invoices.issueDate')}</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button variant="outline" className={cn("w-full pl-3 text-left font-normal", !field.value && "text-muted-foreground")}>
                                  {field.value ? format(field.value, locale === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy') : <span>{translateStr('common.selectDate')}</span>}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar mode="single" selected={field.value} onSelect={(date) => {field.onChange(date); setInvoiceDateMonth(date);}} month={invoiceDateMonth} onMonthChange={setInvoiceDateMonth} initialFocus />
                            </PopoverContent>
                          </Popover>
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="dueDate" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{translateStr('invoices.dueDate')}</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button variant="outline" className={cn("w-full pl-3 text-left font-normal", !field.value && "text-muted-foreground")}>
                                  {field.value ? format(field.value, locale === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy') : <span>{translateStr('common.selectDate')}</span>}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar mode="single" selected={field.value} onSelect={(date) => {field.onChange(date); setDueDateMonth(date);}} month={dueDateMonth} onMonthChange={setDueDateMonth} initialFocus disabled={(date) => date < form.getValues().invoiceDate} />
                            </PopoverContent>
                          </Popover>
                        </FormItem>
                      )} />
                    </div>
                    )}

                    <FormField control={form.control} name="note" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{translateStr('invoices.notes')}</FormLabel>
                        <FormControl><Textarea {...field} placeholder={translateStr('invoices.notesPlaceholder')} className="resize-none h-20" /></FormControl>
                      </FormItem>
                    )} />
                  </form>
                </Form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>{translateStr('invoices.taxes')}</CardTitle></CardHeader>
              <CardContent>
                {taxesLoading ? <Skeleton className="h-6 w-full" /> : (
                  <div className="space-y-3">
                    {taxes.length > 0 ? taxes.map((tax) => (
                      <div key={tax.id} className="flex items-center gap-2">
                        <Checkbox id={`tax-${tax.id}`} checked={tax.selected} onCheckedChange={() => handleToggleTax(tax.id)} />
                        <label htmlFor={`tax-${tax.id}`} className="text-sm font-medium">{tax.name} ({tax.rate}%)</label>
                      </div>
                    )) : <p className="text-sm font-medium">{translateStr('invoices.noTaxes')}</p>}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>{translateStr('invoices.discount')}</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-[2fr_1fr] gap-2 items-center">
                  <Select value={discountType} onValueChange={(v: 'none' | 'percentage' | 'amount') => {setDiscountType(v); if(v==='none') setDiscountValue(0);}}>
                    <SelectTrigger><SelectValue placeholder={translateStr('invoices.selectDiscountType')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{translateStr('invoices.noDiscount')}</SelectItem>
                      <SelectItem value="percentage">{translateStr('invoices.percentageDiscount')}</SelectItem>
                      <SelectItem value="amount">{translateStr('invoices.amountDiscount')}</SelectItem>
                    </SelectContent>
                  </Select>
                  {discountType !== 'none' && (
                    <div className="relative">
                      <Input type="text" inputMode="decimal" value={discountInput} onFocus={(e) => e.target.select()} onChange={(e) => {
                          const raw = e.target.value;
                          setDiscountInput(raw);
                          const num = parseFloat(raw.replace(',', '.'));
                          setDiscountValue(isNaN(num) ? 0 : num);
                        }} className="pr-8" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        {discountType === 'percentage' ? '%' : user.currency_code || '$'}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>{translateStr('invoices.additionalFees')}</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {additionalFees.map((fee, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        type="text"
                        value={fee.description}
                        onChange={(e) => handleAdditionalFeeChange(index, 'description', e.target.value)}
                        placeholder={translateStr('invoices.feeDescriptionPlaceholder')}
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={fee.amount}
                        onChange={(e) => handleAdditionalFeeChange(index, 'amount', parseFloat(e.target.value) || 0)}
                        onFocus={(e) => e.target.select()}
                        placeholder={translateStr('invoices.amount')}
                        className="w-24"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeAdditionalFee(index)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addAdditionalFee}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {translateStr('invoices.addFee')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>{translateStr('invoices.summary')}</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between"><span className="text-sm">{translateStr('invoices.subtotal')}:</span><span className="text-sm font-medium">{formatCurrency(getSubtotal())}</span></div>
                  {discountType !== 'none' && discountValue > 0 && (<div className="flex justify-between text-red-600"><span className="text-sm">{translateStr('invoices.discount')}:</span><span className="text-sm font-medium">-{formatCurrency(getDiscountAmount())}</span></div>)}
                  {taxes.some(tax => tax.selected) && (<div className="flex justify-between"><span className="text-sm">{translateStr('invoices.tax')}:</span><span className="text-sm font-medium">{formatCurrency(getTaxAmount())}</span></div>)}
                  {additionalFees.length > 0 && (<div className="flex justify-between"><span className="text-sm">{translateStr('invoices.additionalFees')}:</span><span className="text-sm font-medium">{formatCurrency(getAdditionalFeesTotal())}</span></div>)}
                  <Separator />
                  <div className="flex justify-between pt-2"><span className="text-base font-bold">{translateStr('invoices.total')}:</span><span className="text-base font-bold">{formatCurrency(getTotal())}</span></div>
                  <div className="mt-6">
                    <Button className="w-full" onClick={form.handleSubmit(onSubmit)} disabled={isCreatingInvoice}>
                      {isCreatingInvoice ? (<><div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />{translateStr('common.loading')}</>) : (<><CreditCard className="h-4 w-4 mr-2" />{translateStr('invoices.buttons.create')}</>)}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <TeacherSelectorDialog 
        open={showTeacherSelector}
        onOpenChange={setShowTeacherSelector}
        teachers={teachers}
        selectedTeacherIds={selectedTeacherIds}
        onSelectTeacher={setSelectedTeacherIds}
        user={user}
        t={t}
      />

      <Footer />
    </div>
  );
};

export default InvoiceTeacherManualCreate;
